import { useEffect } from 'react';
import Modal from 'react-modal';
import { Row, StyledActionBtn } from '../styles';
import {
  Wrapper,
  CloseButton,
  ModalTitle,
  ContentWrapper,
  LeftSide,
  RightSide,
  ButtonsWrapper,
  Input,
  FamilyCodeDescWrapper,
  LeftSideBottomWrapper,
  ModuleLengthSvgWrapper,
  ModuleOptionsWrapper,
  Label,
  FramesInput,
  CheckboxesWrapper,
  ReferenceModuleWrapper,
  RemoveReferenceBtn,
  ReferenceModuleImageWrapper,
  ReferenceModuleInfoWrapper,
  ReferenceModuleImage,
  PlusIcon,
  BespokeIntentTextfield,
  RemoveModuleBtn,
  SelectableReferenceModuleDiv,
  HelperWrapper,
} from './styles';
import FamilyDropdown from '../../FamiliesDropdown';
import ImageViewer from '../../ImageViewer';
import Dropdown from '../../Dropdown';
import Checkbox from '../../Checkbox';
import useObjectElements from '../../../hooks/useObjectElements';
import {
  DESIGN_MATURITY_OPTIONS,
  STANDARDIZATION_INTENT_SCORE_OPTIONS,
  POSITION_OPTIONS,
  VENDOR_OR_EPC_OPTIONS,
  user_roles,
} from '../../../config/enums';
import ModuleTypeRecommendationInput from '../../ModuleTypeRecommendationInput';
import { useUser } from '../../../context/user';
import { DEFAULT_MODULE_LENGTH } from '../consts';
import SecondarySystemsDropdown from '../../SecondarySystemDropdown';
import useGetIdCardByTagId from '../../../hooks/useGetIdCardByTagId';

const formDefaultObject = {
  name: '',
  familyId: null,
  tsCode: '',
  type: '',
  vendor: 'EPC',
  secondarySystem: null,
  otherSecondarySystem: '',
  frameNr: 49,
  length: DEFAULT_MODULE_LENGTH,
  includesPiperack: false,
  includesRiserManifold: false,
  position: null,
  bespoke: false,
  referenceModuleId: null,
  referenceModuleName: '',
  referenceModuleTsCode: '',
  referenceModuleProject: '',
  // estimatedWeight: '',
  expectedMaturityLevel: null,
  // match: null,
  intent: '',
  // not needed on back-end
  family: '',
  secondarySystemId: null,
  referenceModuleImageId: null,
  repeatable: false,
};

const getReferenceModuleFields = (referenceModule) => ({
  referenceModuleId: referenceModule?.id || null,
  referenceModuleTagId: referenceModule?.tagId || null,
  referenceModuleName: referenceModule?.name || '',
  referenceModuleTsCode: referenceModule?.tsCode || '',
  referenceModuleProject: referenceModule?.project || '',
  referenceModuleImageId: referenceModule?.imageId || null,
});

const CreateEditModuleModal = ({
  module,
  defaultFields,
  projectPhase,
  onModalClose,
  isMenuOpen,
  setIsMenuOpen,
  referenceModule,
  setReferenceModule,
  onAddModule,
  onUpdateModule,
  onRemoveModule,
  isAdmin,
  onModuleRecommendationClick,
}) => {
  const {
    object: formData,
    setObjectValue: setFormField,
    setMultipleObjectValues: setMultipleFormFields,
  } = useObjectElements({ initialValues: { ...formDefaultObject } });
  const isEditMode = module !== null;

  const { idCard: referencedIdCard } = useGetIdCardByTagId(referenceModule?.tagId);

  const isSubmitButtonDisabled = !formData.familyId || !formData.family || !formData.tsCode;

  const onRemoveReference = () => {
    setMultipleFormFields(getReferenceModuleFields());
    setReferenceModule(null);
  };

  const handleAddModule = () => {
    let referenceModuleData = {};

    if (formData.bespoke) {
      referenceModuleData = { ...getReferenceModuleFields() };
    }

    if (formData.secondarySystem === 'Other') {
      formData.secondarySystem = formData.otherSecondarySystem;
    }
    delete formData.otherSecondarySystem;
    const addModuleData = {
      ...formData,
      ...referenceModuleData,
    };

    onAddModule(addModuleData);
  };

  const handleUpdateModule = () => {
    if (formData.secondarySystem === 'Other') {
      formData.secondarySystem = formData.otherSecondarySystem;
    }
    delete formData.otherSecondarySystem;
    onUpdateModule(formData);
  };

  useEffect(() => {
    setMultipleFormFields(getReferenceModuleFields(referenceModule));
  }, [referenceModule]);

  const onSyncReferenceModuleDataWithCurrentModule = () => {
    console.log(referencedIdCard);
    setMultipleFormFields({
      referenceModuleName: referencedIdCard.name,
      referenceModuleTsCode: referencedIdCard.tsCode,
      tsCode: formData.tsCode || referencedIdCard.tsCode,
      name: formData.name || referencedIdCard.name,
      secondarySystem: referencedIdCard.secondarySystem,
      secondarySystemId: referencedIdCard.secondarySystemId,
      includesPiperack: referencedIdCard.integratedPiperack === 'Yes' ? true : false,
    });
  };

  useEffect(() => {
    if (referencedIdCard === undefined) {
      return;
    }
    window.requestAnimationFrame(() => {
      onSyncReferenceModuleDataWithCurrentModule();
    });
  }, [referencedIdCard?.id]);

  useEffect(() => {
    let moduleFieldsToBeSet = {};
    if (module) {
      moduleFieldsToBeSet = { ...module, intent: module?.scoringTable?.intent || '' };
    }
    if (defaultFields?.referenceModule) {
      moduleFieldsToBeSet = { ...moduleFieldsToBeSet, ...getReferenceModuleFields(defaultFields?.referenceModule) };
    }
    moduleFieldsToBeSet = { ...moduleFieldsToBeSet, ...defaultFields };

    setMultipleFormFields(moduleFieldsToBeSet);
  }, []);

  return (
    <Modal
      isOpen={true}
      shouldCloseOnOverlayClick={false}
      shouldCloseOnEsc
      onRequestClose={onModalClose}
      style={{
        content: {
          left: isMenuOpen ? 'calc(50% + 200px)' : '50%',
          transition: 'left 0.5s ease-out',
        },
      }}>
      <Wrapper>
        <ModalTitle>{isEditMode ? 'Edit module:' : 'Add new module:'}</ModalTitle>
        <CloseButton onClick={() => onModalClose()} />
        <ContentWrapper>
          <LeftSide>
            <FamilyCodeDescWrapper>
              <FamilyDropdown
                isDisabled={!isAdmin || isEditMode}
                familyId={formData.familyId}
                setDataFunc={(obj) =>
                  setMultipleFormFields({
                    familyId: obj?.familyId || null,
                    family: obj?.family || '',
                  })
                }
              />
              <Input
                placeholder='Enter module TS-code'
                value={formData.tsCode}
                onChange={(e) => setFormField('tsCode', e.target.value)}
                disabled={!isAdmin}
              />
              <Input
                placeholder='Enter module description / type of module'
                value={formData.name}
                onChange={(e) => setFormField('name', e.target.value)}
                disabled={!isAdmin}
              />
            </FamilyCodeDescWrapper>
            <Row marginBottom='0.5rem'>
              <Label>Secondary system:</Label>
            </Row>
            <Row marginBottom='1rem'>
              <SecondarySystemsDropdown
                value={formData.secondarySystem}
                setDataFunc={setMultipleFormFields}
                isDisabled={!isAdmin}
                hasOtherOption={true}
                setOtherData={(secondarySystemFields) => setMultipleFormFields(secondarySystemFields)}
              />
            </Row>
            {formData.secondarySystemId === 'Other' && (
              <Row marginBottom='1rem'>
                <Input
                  placeholder='Secondary System'
                  value={formData.otherSecondarySystem}
                  onChange={(e) => setFormField('otherSecondarySystem', e.target.value)}
                  disabled={!isAdmin}
                />
              </Row>
            )}
            <Row marginBottom='0.5rem'>
              <Label>Vendor or EPC</Label>
            </Row>
            <Row marginBottom='2rem'>
              <Dropdown
                value={formData.vendor}
                options={VENDOR_OR_EPC_OPTIONS}
                placeholder='Vendor or EPC:'
                onChange={(option) => setFormField('vendor', option.value)}
                isDisabled={!isAdmin}
              />
            </Row>
            <LeftSideBottomWrapper>
              {/* <ModuleLengthSvgWrapper></ModuleLengthSvgWrapper> */}
              <ModuleOptionsWrapper>
                {/* <Row
                  marginBottom='0.5rem'
                  justify='space-between'>
                  <HelperWrapper>
                    <Label>Length:</Label>
                  </HelperWrapper>
                  <HelperWrapper>
                    <Label>Position:</Label>
                  </HelperWrapper>
                </Row> */}
                <Row
                  marginBottom='0.5rem'
                  justify='space-between'>
                  <HelperWrapper>
                    <Label marginBottom='0.5rem'>Frame nr:</Label>
                    <FramesInput
                      placeholder='49'
                      value={formData.frameNr}
                      onChange={(e) => setFormField('frameNr', e.target.value)}
                      disabled={!isAdmin}
                    />
                  </HelperWrapper>
                  <HelperWrapper>
                    <Label marginBottom='0.5rem'>Length:</Label>
                    <FramesInput
                      placeholder='5'
                      value={formData.length}
                      onChange={(e) => setFormField('length', e.target.value)}
                      disabled={!isAdmin}
                    />
                  </HelperWrapper>
                  <HelperWrapper>
                    <Label marginBottom='0.5rem'>Position:</Label>
                    <Dropdown
                      value={formData.position}
                      placeholder='Position'
                      options={POSITION_OPTIONS}
                      isOptional
                      onChange={(newOption) => setFormField('position', newOption.value)}
                      isDisabled={!isAdmin}
                    />
                  </HelperWrapper>
                </Row>
                <CheckboxesWrapper>
                  <Row
                    marginBottom='0.5rem'
                    justify='start'>
                    <Checkbox
                      classes='checkbox'
                      value={formData.includesPiperack}
                      onChange={(val) => setFormField('includesPiperack', val)}
                      isDisabled={!isAdmin}
                    />
                    <Label>Includes piperack</Label>
                  </Row>
                  <Row
                    marginBottom='0.5rem'
                    justify='start'>
                    <Checkbox
                      classes='checkbox'
                      value={formData.includesRiserManifold}
                      onChange={(val) => setFormField('includesRiserManifold', val)}
                      isDisabled={!isAdmin}
                    />
                    <Label>Includes riser manifold</Label>
                  </Row>
                </CheckboxesWrapper>
              </ModuleOptionsWrapper>
            </LeftSideBottomWrapper>
          </LeftSide>
          <RightSide>
            <Row
              marginBottom='1rem'
              justify='start'>
              <Checkbox
                classes='checkbox'
                value={formData.bespoke}
                onChange={(newVal) => setFormField('bespoke', newVal)}
                isDisabled={!isAdmin}
              />
              <Label>Bespoke (explicit check that no Reference module will be added)</Label>
            </Row>
            {!formData.bespoke && (
              <ReferenceModuleWrapper>
                <Row
                  justify='space-between'
                  marginBottom='0.5rem'>
                  <Label>Reference Module:</Label>
                  {formData.referenceModuleId && isAdmin && (
                    <RemoveReferenceBtn
                      title='remove this reference'
                      onClick={() => onRemoveReference()}
                    />
                  )}
                </Row>
                <Row marginBottom='1.5rem'>
                  <ReferenceModuleImageWrapper>
                    {formData.referenceModuleId || !isAdmin ? (
                      <ImageViewer
                        imageId={formData.referenceModuleImageId}
                        fullscreen={false}
                        defaultLoaderSize={75}
                        containerStyle={{
                          width: '100%',
                          height: '100%',
                        }}
                      />
                    ) : (
                      <SelectableReferenceModuleDiv onClick={() => setIsMenuOpen(true)}>
                        <PlusIcon />
                      </SelectableReferenceModuleDiv>
                    )}
                  </ReferenceModuleImageWrapper>
                  <ReferenceModuleInfoWrapper>
                    <Input
                      value={formData.referenceModuleProject}
                      placeholder='Project Name'
                      onChange={(e) => setFormField('referenceModuleProject', e.target.value)}
                      disabled={Boolean(formData.referenceModuleId) || !isAdmin}
                    />
                    <ModuleTypeRecommendationInput
                      value={formData.referenceModuleName}
                      onChange={(val, isRecommendationClick) => {
                        // if onModuleRecommendationClick then val is card from sidemenu else it is the name of the card
                        if (isRecommendationClick) {
                          onModuleRecommendationClick(val);
                        } else {
                          setFormField('referenceModuleName', val);
                        }
                      }}
                      // onChange={(card, isRecommendationClick) => {
                      //   // if onModuleRecommendationClick then val is card from sidemenu else it is the name of the card

                      //   if (isRecommendationClick) {
                      //     onModuleRecommendationClick(card);
                      //     return;
                      //   }

                      //   setFormField('referenceModuleName', card?.name);
                      // }}
                      // isDisabled={Boolean(formData.referenceModuleId) || canEdit}
                    />
                  </ReferenceModuleInfoWrapper>
                </Row>
              </ReferenceModuleWrapper>
            )}
            {/* <Row marginBottom='0.5rem'>
              <Label>Estimated Weight (tons):</Label>
            </Row>
            <Row
              width='60%'
              marginBottom='1rem'>
              <Input
                type='number'
                value={formData.estimatedWeight}
                onChange={(e) => setFormField('estimatedWeight', e.target.value)}
                disabled={!isAdmin}
              />
            </Row> */}
            {/* {!formData.bespoke && (
              <>
                <Row marginBottom='0.5rem'>
                  <Label>Standardization Match Score:</Label>
                </Row>
                <Row
                  width='60%'
                  marginBottom='1.5rem'>
                  <Dropdown
                    value={formData.match}
                    options={STANDARDIZATION_INTENT_SCORE_OPTIONS}
                    placeholder='Intent Score'
                    onChange={(option) => setFormField('match', option.value)}
                    isDisabled={!canEdit}
                  />
                </Row> 
                </>
            )} */}
            {formData.bespoke && <Row marginBottom='0.5rem' justify='start'>
              <Checkbox
                  classes='checkbox'
                  value={formData.repeatable}
                  onChange={(newVal) => setFormField('repeatable', newVal)}
                  isDisabled={!isAdmin}/><Label>Repeatable</Label>
            </Row>}
            <Row marginBottom='0.5rem'>
              <Label>{formData.bespoke ? 'Opportunity for repeatability intent' : 'Standardization intent'}:</Label>
            </Row>
            <BespokeIntentTextfield
              value={formData.intent}
              onChange={(e) => setFormField('intent', e.target.value)}
              disabled={!isAdmin}
            />
            {!formData.bespoke && (
              <>
                <Row marginBottom='0.5rem'>
                  <Label>Expected maturity level at contract date:</Label>
                </Row>
                <Row
                  width='60%'
                  marginBottom='1rem'>
                  <Dropdown
                    value={formData.expectedMaturityLevel}
                    options={DESIGN_MATURITY_OPTIONS}
                    placeholder='Design Maturity'
                    onChange={(option) => setFormField('expectedMaturityLevel', option.value)}
                    isDisabled={!isAdmin}
                    // isDisabled={!(isAdmin && ((isEditMode && isInitPhase) || !isEditMode))}
                  />
                </Row>
              </>
            )}
          </RightSide>
        </ContentWrapper>
        {isAdmin && (
          <ButtonsWrapper>
            {isEditMode ? (
              <>
                <RemoveModuleBtn
                  title='Remove Module'
                  onClick={() => onRemoveModule(module)}
                />
                <StyledActionBtn
                  width='250px'
                  title='Update Module'
                  onClick={handleUpdateModule}
                />
              </>
            ) : (
              <StyledActionBtn
                disabled={isSubmitButtonDisabled}
                width='250px'
                title='Add Module'
                onClick={handleAddModule}
              />
            )}
          </ButtonsWrapper>
        )}
      </Wrapper>
    </Modal>
  );
};

export default CreateEditModuleModal;
